// biome-ignore-all lint/a11y: TODO - address a11y issues later
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ChevronDown,
  ChevronRight,
  Download,
  GripVertical,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  Upload,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ExportFilenameDialog } from "@/components/export-filename-dialog";
import { RecursiveFilterBuilder } from "@/components/filter-builder/recursive-filter-builder";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  useCreateRule,
  useCreateRuleSet,
  useDeleteRule,
  useDeleteRuleSet,
  useExportRuleSet,
  useImportRuleSet,
  useReorderRules,
  useRuleFieldDefinitions,
  useRuleSets,
  useRules,
  useUpdateRule,
  useUpdateRuleSet,
} from "@/hooks/api/use-channel-tagging-rules";
import {
  createGroupFieldFilter,
  enrichFieldDefinitionsWithRecordType,
} from "@/hooks/api/use-channel-tagging-rules.utils";
import type { AvailableFilterField } from "@/hooks/use-available-filter-fields";
import { APIError } from "@/lib/api";
import { downloadJSON, sanitizeFilename } from "@/lib/file-download";
import type { FilterGroup, FilterOperator } from "@/lib/filters";
import { EMPTY_GROUP } from "@/lib/filters";
import type {
  ChannelTaggingRule,
  ChannelTaggingRuleSet,
} from "@/types/channel-tagging-rules";

export function RulesManager() {
  const [selectedRuleSet, setSelectedRuleSet] =
    useState<ChannelTaggingRuleSet | null>(null);
  const [expandedRuleSet, setExpandedRuleSet] = useState<string | null>(null);
  const [ruleSetDialog, setRuleSetDialog] = useState<{
    open: boolean;
    mode: "create" | "edit";
    data?: ChannelTaggingRuleSet;
  }>({ open: false, mode: "create" });
  const [ruleDialog, setRuleDialog] = useState<{
    open: boolean;
    mode: "create" | "edit";
    data?: ChannelTaggingRule;
  }>({ open: false, mode: "create" });
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
  } | null>(null);
  const [exportDialog, setExportDialog] = useState<{
    open: boolean;
    ruleSet: ChannelTaggingRuleSet | null;
  }>({ open: false, ruleSet: null });

  const { data: ruleSets, isLoading } = useRuleSets();
  const { data: rules, isLoading: isLoadingRules } = useRules(
    selectedRuleSet?.id ?? null,
  );
  const createRuleSet = useCreateRuleSet();
  const updateRuleSet = useUpdateRuleSet();
  const deleteRuleSet = useDeleteRuleSet();
  const createRule = useCreateRule();
  const updateRule = useUpdateRule();
  const deleteRule = useDeleteRule();
  const reorderRules = useReorderRules(selectedRuleSet?.id ?? "");
  const exportRuleSet = useExportRuleSet();
  const importRuleSet = useImportRuleSet();

  // Setup sensors for drag-and-drop
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 6,
      },
    }),
  );

  const handleCreateRuleSet = () => {
    setRuleSetDialog({ open: true, mode: "create" });
  };

  const handleEditRuleSet = (ruleSet: ChannelTaggingRuleSet) => {
    setRuleSetDialog({ open: true, mode: "edit", data: ruleSet });
  };

  const handleEditRule = (rule: ChannelTaggingRule) => {
    setRuleDialog({ open: true, mode: "edit", data: rule });
  };

  const handleDeleteRuleSet = (ruleSet: ChannelTaggingRuleSet) => {
    setConfirmDialog({
      open: true,
      title: "Delete Rule Set",
      description: `Are you sure you want to delete "${ruleSet.name}"? This action cannot be undone and will delete all rules within this set.`,
      onConfirm: () => {
        deleteRuleSet.mutate(ruleSet.id, {
          onSuccess: () => {
            toast.success("Rule set deleted");
            if (selectedRuleSet?.id === ruleSet.id) {
              setSelectedRuleSet(null);
            }
          },
          onError: () => toast.error("Failed to delete rule set"),
        });
        setConfirmDialog(null);
      },
    });
  };

  const handleCreateRule = () => {
    if (!selectedRuleSet) return;
    setRuleDialog({ open: true, mode: "create" });
  };

  const handleDeleteRule = (rule: ChannelTaggingRule) => {
    if (!selectedRuleSet) return;
    setConfirmDialog({
      open: true,
      title: "Delete Rule",
      description: `Are you sure you want to delete this rule (priority ${rule.priority})? This action cannot be undone.`,
      onConfirm: () => {
        deleteRule.mutate(
          { ruleSetId: selectedRuleSet.id, ruleId: rule.id },
          {
            onSuccess: () => toast.success("Rule deleted"),
            onError: () => toast.error("Failed to delete rule"),
          },
        );
        setConfirmDialog(null);
      },
    });
  };

  const handleExportRuleSet = (ruleSet: ChannelTaggingRuleSet) => {
    setExportDialog({ open: true, ruleSet });
  };

  const handleExportWithFilename = (filename: string) => {
    if (!exportDialog.ruleSet) return;

    exportRuleSet.mutate(
      { ruleSetId: exportDialog.ruleSet.id },
      {
        onSuccess: (data) => {
          downloadJSON(data, filename);
          toast.success("Rule set exported");
          setExportDialog({ open: false, ruleSet: null });
        },
        onError: () => {
          toast.error("Failed to export rule set");
          // Close dialog on error to prevent it from staying open
          setExportDialog({ open: false, ruleSet: null });
        },
      },
    );
  };

  const handleImportRuleSet = () => {
    // Create a file input element
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      try {
        const text = await file.text();
        const data = JSON.parse(text);

        // Basic validation
        if (!data.ruleset || !data.ruleset.rules) {
          toast.error("Invalid JSON structure: missing 'ruleset.rules'");
          return;
        }

        importRuleSet.mutate(data, {
          onSuccess: (createdRuleSets) => {
            toast.success(`Imported ${createdRuleSets.length} rule set(s)`);
            // Expand the first created rule set
            if (createdRuleSets.length > 0) {
              const firstRuleSet = createdRuleSets[0];
              setExpandedRuleSet(firstRuleSet.id);
              setSelectedRuleSet(firstRuleSet);
            }
          },
          onError: (error) => {
            // Extract error message from APIError if available
            let errorMessage = "Failed to import rule set";
            if (error instanceof APIError && error.data) {
              const data = error.data as { error?: string };
              errorMessage = data.error || error.message;
            } else if (error instanceof Error) {
              errorMessage = error.message;
            }
            toast.error(errorMessage);
          },
        });
      } catch {
        toast.error("Failed to read or parse JSON file");
      }
    };
    input.click();
  };

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id || !rules || !selectedRuleSet) {
        return;
      }

      const oldIndex = rules.findIndex((rule) => rule.id === active.id);
      const newIndex = rules.findIndex((rule) => rule.id === over.id);

      if (oldIndex === -1 || newIndex === -1) return;

      // Reorder locally using arrayMove
      const reorderedRules = arrayMove(rules, oldIndex, newIndex);
      const ruleIds = reorderedRules.map((rule) => rule.id);

      // Call the reorder mutation
      reorderRules.mutate(ruleIds, {
        onError: () => {
          toast.error("Failed to reorder rules");
        },
      });
    },
    [rules, selectedRuleSet, reorderRules],
  );

  const toggleExpand = (ruleSetId: string) => {
    if (expandedRuleSet === ruleSetId) {
      setExpandedRuleSet(null);
      setSelectedRuleSet(null);
    } else {
      setExpandedRuleSet(ruleSetId);
      const ruleSet = ruleSets?.find((rs) => rs.id === ruleSetId);
      setSelectedRuleSet(ruleSet ?? null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Rule Sets</h2>
        <div className="flex items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={handleImportRuleSet}
                size="sm"
                variant="outline"
                disabled={importRuleSet.isPending}
              >
                <Upload className="h-4 w-4 mr-2" />
                Import
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Import rule set from JSON file</p>
            </TooltipContent>
          </Tooltip>
          <Button onClick={handleCreateRuleSet} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            New Rule Set
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {ruleSets?.map((ruleSet) => (
          <Card key={ruleSet.id} className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 flex-1">
                <button
                  onClick={() => toggleExpand(ruleSet.id)}
                  className="p-1 hover:bg-muted rounded"
                >
                  {expandedRuleSet === ruleSet.id ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </button>
                <div>
                  <div className="font-medium">{ruleSet.name}</div>
                  {ruleSet.description && (
                    <div className="text-sm text-muted-foreground">
                      {ruleSet.description}
                    </div>
                  )}
                </div>
                {ruleSet.is_default && (
                  <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded">
                    Default
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleExportRuleSet(ruleSet)}
                      disabled={exportRuleSet.isPending}
                    >
                      {exportRuleSet.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Export rule set as JSON</p>
                  </TooltipContent>
                </Tooltip>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEditRuleSet(ruleSet)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteRuleSet(ruleSet)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {expandedRuleSet === ruleSet.id && (
              <div className="mt-4 pt-4 border-t space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold">Rules</h3>
                  <Button
                    onClick={handleCreateRule}
                    size="sm"
                    variant="outline"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Rule
                  </Button>
                </div>

                {isLoadingRules ? (
                  <div className="space-y-2">
                    <Skeleton className="h-20 w-full" />
                    <Skeleton className="h-20 w-full" />
                    <Skeleton className="h-20 w-full" />
                  </div>
                ) : rules && rules.length > 0 ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={rules.map((r) => r.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-2">
                        {rules.map((rule) => (
                          <DraggableRuleCard
                            key={rule.id}
                            rule={rule}
                            onEdit={() => handleEditRule(rule)}
                            onDelete={() => handleDeleteRule(rule)}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                ) : (
                  <div className="text-sm text-muted-foreground text-center py-4">
                    No rules yet. Create one to get started.
                  </div>
                )}
              </div>
            )}
          </Card>
        ))}
      </div>

      {(!ruleSets || ruleSets.length === 0) && (
        <Card className="p-8 text-center text-muted-foreground">
          No rule sets yet. Create one to get started.
        </Card>
      )}

      <RuleSetDialog
        open={ruleSetDialog.open}
        mode={ruleSetDialog.mode}
        data={ruleSetDialog.data}
        onClose={() => setRuleSetDialog({ open: false, mode: "create" })}
        onCreate={(data) => {
          createRuleSet.mutate(data, {
            onSuccess: () => {
              toast.success("Rule set created");
              setRuleSetDialog({ open: false, mode: "create" });
            },
            onError: () => toast.error("Failed to create rule set"),
          });
        }}
        onUpdate={(id, data) => {
          updateRuleSet.mutate(
            { ruleSetId: id, data },
            {
              onSuccess: () => {
                toast.success("Rule set updated");
                setRuleSetDialog({ open: false, mode: "create" });
              },
              onError: () => toast.error("Failed to update rule set"),
            },
          );
        }}
      />

      <RuleDialog
        open={ruleDialog.open}
        mode={ruleDialog.mode}
        data={ruleDialog.data}
        onClose={() => setRuleDialog({ open: false, mode: "create" })}
        onCreate={(data) => {
          if (!selectedRuleSet) return;
          // Calculate next priority based on existing rules
          const maxPriority =
            rules && rules.length > 0
              ? Math.max(...rules.map((r) => r.priority))
              : 0;
          const nextPriority = maxPriority + 1;

          createRule.mutate(
            {
              ruleSetId: selectedRuleSet.id,
              data: { ...data, priority: nextPriority },
            },
            {
              onSuccess: () => {
                toast.success("Rule created");
                setRuleDialog({ open: false, mode: "create" });
              },
              onError: () => toast.error("Failed to create rule"),
            },
          );
        }}
        onUpdate={(id, data) => {
          if (!selectedRuleSet) return;
          updateRule.mutate(
            { ruleSetId: selectedRuleSet.id, ruleId: id, data },
            {
              onSuccess: () => {
                toast.success("Rule updated");
                setRuleDialog({ open: false, mode: "create" });
              },
              onError: () => toast.error("Failed to update rule"),
            },
          );
        }}
      />

      <AlertDialog
        open={confirmDialog?.open ?? false}
        onOpenChange={(open) => !open && setConfirmDialog(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmDialog?.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog?.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmDialog(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDialog?.onConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ExportFilenameDialog
        open={exportDialog.open}
        onOpenChange={(open) =>
          !open && setExportDialog({ open: false, ruleSet: null })
        }
        defaultFilename={
          exportDialog.ruleSet
            ? `${sanitizeFilename(exportDialog.ruleSet.name)}-rules`
            : ""
        }
        extension="json"
        onExport={handleExportWithFilename}
        disabled={exportRuleSet.isPending}
      />
    </div>
  );
}

// Draggable Rule Card Component
function DraggableRuleCard({
  rule,
  onEdit,
  onDelete,
}: {
  rule: ChannelTaggingRule;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: rule.id,
    animateLayoutChanges: () => false,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card className="p-3 bg-muted/30">
        <div className="flex items-start gap-2">
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing pt-1 text-muted-foreground hover:text-foreground"
          >
            <GripVertical className="h-4 w-4" />
          </div>
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">
                Priority: {rule.priority}
              </span>
              {rule.description && (
                <span className="text-sm text-muted-foreground">
                  - {rule.description}
                </span>
              )}
            </div>
            <div className="text-xs space-y-1">
              {rule.labels.map((label) => (
                <div key={label.id}>
                  <span className="font-medium">{label.label_field}:</span>{" "}
                  {label.label_value}
                </div>
              ))}
            </div>
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground">
                Conditions
              </summary>
              <pre className="mt-1 p-2 bg-background rounded overflow-auto">
                {JSON.stringify(rule.conditions, null, 2)}
              </pre>
            </details>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={onEdit}>
              <Pencil className="h-3 w-3" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onDelete}>
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

// Rule Set Dialog Component
function RuleSetDialog({
  open,
  mode,
  data,
  onClose,
  onCreate,
  onUpdate,
}: {
  open: boolean;
  mode: "create" | "edit";
  data?: ChannelTaggingRuleSet;
  onClose: () => void;
  onCreate: (data: {
    name: string;
    description?: string;
    is_default?: boolean;
  }) => void;
  onUpdate: (
    id: string,
    data: { name?: string; description?: string; is_default?: boolean },
  ) => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isDefault, setIsDefault] = useState(false);

  // Populate form when dialog opens with data
  useEffect(() => {
    if (open && data) {
      setName(data.name);
      setDescription(data.description || "");
      setIsDefault(data.is_default);
    } else if (open && !data) {
      // Reset for create mode
      setName("");
      setDescription("");
      setIsDefault(false);
    }
  }, [open, data]);

  const handleSubmit = () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }

    if (mode === "create") {
      onCreate({ name, description, is_default: isDefault });
    } else if (data) {
      onUpdate(data.id, { name, description, is_default: isDefault });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-card">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Create Rule Set" : "Edit Rule Set"}
          </DialogTitle>
          <DialogDescription>
            A rule set is a collection of rules for tagging custom channels.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Paid Media Rules"
            />
          </div>
          <div>
            <Label htmlFor="description">Description (optional)</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What does this rule set do?"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="is_default"
              checked={isDefault}
              onCheckedChange={(checked) => setIsDefault(checked === true)}
            />
            <Label htmlFor="is_default" className="cursor-pointer">
              Set as default rule set
            </Label>
          </div>
          <span className="text-xs text-muted-foreground">
            The default rule set is automatically selected for new sessions.
          </span>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>
            {mode === "create" ? "Create" : "Update"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Rule Dialog Component
function RuleDialog({
  open,
  mode,
  data,
  onClose,
  onCreate,
  onUpdate,
}: {
  open: boolean;
  mode: "create" | "edit";
  data?: ChannelTaggingRule;
  onClose: () => void;
  onCreate: (data: {
    description?: string;
    conditions: Record<string, unknown>;
    labels: Array<{ label_field: string; label_value: string }>;
  }) => void;
  onUpdate: (
    id: string,
    data: {
      priority?: number;
      description?: string;
      conditions?: Record<string, unknown>;
      labels?: Array<{ label_field: string; label_value: string }>;
    },
  ) => void;
}) {
  const [description, setDescription] = useState("");
  const [conditions, setConditions] = useState<FilterGroup>(EMPTY_GROUP);
  const [customChannel, setCustomChannel] = useState("");
  const [customSubChannel, setCustomSubChannel] = useState("");
  const [useJsonEditor, setUseJsonEditor] = useState(false);
  const [conditionsJson, setConditionsJson] = useState("{}");
  const [jsonError, setJsonError] = useState<string | null>(null);

  // Fetch field definitions for FilterBuilder
  const {
    data: fieldDefsData,
    isLoading: loadingFields,
    error: fieldDefsError,
  } = useRuleFieldDefinitions();

  // Validate JSON in real-time
  useEffect(() => {
    if (!useJsonEditor) {
      setJsonError(null);
      return;
    }

    if (!conditionsJson.trim()) {
      setJsonError(null);
      return;
    }

    try {
      JSON.parse(conditionsJson);
      setJsonError(null);
    } catch (e) {
      setJsonError(e instanceof Error ? e.message : "Invalid JSON");
    }
  }, [conditionsJson, useJsonEditor]);

  // Transform field definitions to AvailableFilterField format
  // First enrich with record_type metadata, then transform
  // Converts backend {value, label} format to frontend {id, label} format
  const allFieldDefinitions = useMemo<AvailableFilterField[]>(() => {
    if (!fieldDefsData) return [];
    const enrichedFields = enrichFieldDefinitionsWithRecordType(fieldDefsData);
    return enrichedFields.map((field) => {
      // Transform enum_values from backend format to frontend SelectOption format
      // Backend can return string[] or {value, label}[]
      let transformedEnumValues:
        | string[]
        | Array<{ id: string; label: string }> = [];
      if (field.enum_values && Array.isArray(field.enum_values)) {
        if (field.enum_values.length > 0) {
          const firstVal = field.enum_values[0];
          if (
            typeof firstVal === "object" &&
            "value" in firstVal &&
            "label" in firstVal
          ) {
            // Convert {value, label} to {id, label}
            transformedEnumValues = (
              field.enum_values as Array<{ value: string; label: string }>
            ).map((ev) => ({ id: ev.value, label: ev.label }));
          } else {
            // Already string[]
            transformedEnumValues = field.enum_values as string[];
          }
        }
      }

      return {
        id: field.id,
        label: field.display_name,
        dataType: field.data_type,
        hasEnumValues: field.has_enum_values,
        enumValues: transformedEnumValues,
        validOperators: field.valid_operators as FilterOperator[],
        isCustom: field.is_custom,
      };
    });
  }, [fieldDefsData]);

  // Create a per-group field filter function
  // Each nested FilterGroup will use this to get fields relevant to its own record_type filters
  const getFieldsForGroup = useMemo(() => {
    if (allFieldDefinitions.length === 0) return undefined;
    return createGroupFieldFilter(allFieldDefinitions);
  }, [allFieldDefinitions]);

  // Populate form when dialog opens with data
  useEffect(() => {
    if (open && data) {
      setDescription(data.description || "");
      // Parse conditions from JSON to FilterGroup
      try {
        const cond = data.conditions as unknown;

        if (
          !cond ||
          (typeof cond === "object" && Object.keys(cond).length === 0)
        ) {
          // Empty conditions
          setConditions(EMPTY_GROUP);
        } else if (
          cond &&
          typeof cond === "object" &&
          "operator" in cond &&
          "filters" in cond
        ) {
          // It's a FilterGroup - load it directly
          setConditions(cond as FilterGroup);
        } else if (
          cond &&
          typeof cond === "object" &&
          "field" in cond &&
          "op" in cond
        ) {
          // Single StructuredFilter - wrap it in a FilterGroup
          setConditions({
            operator: "AND",
            filters: [cond],
          } as FilterGroup);
        } else {
          // Unknown format
          setConditions(EMPTY_GROUP);
        }

        // Always sync JSON representation
        setConditionsJson(JSON.stringify(data.conditions, null, 2));
      } catch {
        setConditions(EMPTY_GROUP);
        setConditionsJson("{}");
      }
      setCustomChannel(
        data.labels.find((l) => l.label_field === "custom_channel")
          ?.label_value || "",
      );
      setCustomSubChannel(
        data.labels.find((l) => l.label_field === "custom_sub_channel")
          ?.label_value || "",
      );
    } else if (open && !data) {
      // Reset for create mode
      setDescription("");
      setConditions(EMPTY_GROUP);
      setConditionsJson("{}");
      setUseJsonEditor(false);
      setCustomChannel("");
      setCustomSubChannel("");
    }
  }, [open, data]);

  const handleSubmit = () => {
    // Validate required fields
    if (!customChannel.trim()) {
      toast.error("Custom Channel is required");
      return;
    }

    if (!customSubChannel.trim()) {
      toast.error("Custom Sub-Channel is required");
      return;
    }

    const labels = [
      {
        label_field: "custom_channel",
        label_value: customChannel.trim(),
      },
      {
        label_field: "custom_sub_channel",
        label_value: customSubChannel.trim(),
      },
    ];

    // Get conditions based on which editor is active
    let conditionsToSave: Record<string, unknown>;

    if (useJsonEditor) {
      // Check for JSON errors first
      if (jsonError) {
        toast.error("Please fix JSON errors before saving");
        return;
      }

      // Parse JSON when using JSON editor
      try {
        conditionsToSave = JSON.parse(conditionsJson);
      } catch {
        toast.error("Invalid JSON in conditions");
        return;
      }
    } else {
      // Use visual editor state
      // FilterGroup is a plain object that serializes correctly to JSON.
      // The backend expects Record<string, unknown> for flexibility.
      // We serialize to JSON and back to get a plain object that satisfies the type.
      conditionsToSave = JSON.parse(JSON.stringify(conditions));
    }

    if (mode === "create") {
      onCreate({
        description,
        conditions: conditionsToSave,
        labels,
      });
    } else if (data) {
      onUpdate(data.id, {
        description,
        conditions: conditionsToSave,
        labels,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto bg-card">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Create Rule" : "Edit Rule"}
          </DialogTitle>
          <DialogDescription>
            Define conditions and output labels for channel tagging.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground">
              Priority is determined by rule order in the list. Drag rules to
              reorder.
            </p>
          </div>
          <div>
            <Label htmlFor="custom_channel">
              Custom Channel <span className="text-destructive">*</span>
            </Label>
            <Input
              id="custom_channel"
              value={customChannel}
              onChange={(e) => setCustomChannel(e.target.value)}
              placeholder="e.g., Paid Search"
            />
          </div>
          <div>
            <Label htmlFor="custom_sub_channel">
              Custom Sub-Channel <span className="text-destructive">*</span>
            </Label>
            <Input
              id="custom_sub_channel"
              value={customSubChannel}
              onChange={(e) => setCustomSubChannel(e.target.value)}
              placeholder="e.g., Google Ads"
            />
          </div>
          <div>
            <Label htmlFor="description">Description (optional)</Label>
            <Input
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What does this rule match?"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Conditions</Label>
              {!loadingFields &&
                !fieldDefsError &&
                allFieldDefinitions.length > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      // Sync state when toggling
                      if (!useJsonEditor) {
                        // Switching to JSON - save current visual editor state
                        setConditionsJson(JSON.stringify(conditions, null, 2));
                      } else {
                        // Switching to visual - parse JSON
                        try {
                          const parsed = JSON.parse(conditionsJson);
                          setConditions(parsed as FilterGroup);
                        } catch {
                          toast.error(
                            "Invalid JSON - cannot switch to visual editor",
                          );
                          return;
                        }
                      }
                      setUseJsonEditor(!useJsonEditor);
                    }}
                    className="text-xs"
                  >
                    {useJsonEditor
                      ? "Switch to Visual Editor"
                      : "Switch to JSON Editor"}
                  </Button>
                )}
            </div>
            {loadingFields ? (
              <Skeleton className="h-10 w-full" />
            ) : fieldDefsError ? (
              <div className="p-4 border border-destructive rounded-md bg-destructive/10">
                <p className="text-sm text-destructive font-medium">
                  Failed to load field definitions
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {fieldDefsError instanceof Error
                    ? fieldDefsError.message
                    : "Unable to fetch available fields for rule conditions"}
                </p>
              </div>
            ) : allFieldDefinitions.length === 0 ? (
              <div className="p-4 border border-muted rounded-md bg-muted/30">
                <p className="text-sm text-muted-foreground">
                  No fields available. Please try refreshing the page.
                </p>
              </div>
            ) : useJsonEditor ? (
              <>
                <Textarea
                  value={conditionsJson}
                  onChange={(e) => setConditionsJson(e.target.value)}
                  className={`font-mono text-xs ${
                    jsonError
                      ? "border-destructive focus-visible:ring-destructive"
                      : ""
                  }`}
                  rows={20}
                  placeholder='{"operator": "AND", "filters": [...]}'
                />
                {jsonError ? (
                  <p className="text-xs text-destructive mt-1">{jsonError}</p>
                ) : (
                  <p className="text-xs text-muted-foreground mt-1">
                    JSON editor for power users. Supports complex nested
                    conditions with multiple operator levels.
                  </p>
                )}
              </>
            ) : (
              <>
                <RecursiveFilterBuilder
                  availableFields={allFieldDefinitions}
                  value={conditions}
                  onChange={setConditions}
                  getFieldsForGroup={getFieldsForGroup}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Visual editor with support for nested groups. Use "Add group"
                  to create complex conditions like (A OR B) AND (C OR D).
                </p>
              </>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={useJsonEditor && !!jsonError}
          >
            {mode === "create" ? "Create" : "Update"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
